import React, { useState, useEffect, useRef } from "react";
import {
  FaEdit,
  FaTrash,
  FaVideo,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import DatePicker from "react-datepicker";
import axios from "axios";
import {
  addOccasion,
  getOccasionsByEventId,
  deleteOccasionData,
  updateOccasion,
  FRONTEND_URL,
} from "../../server/Api";
import { getLookupUtils } from "../../utils/lookup";
import "../../assets/css/occasion.css";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import * as XLSX from "sheetjs-style";
import {
  canEditPage,
  canDeletePage,
  canSpecialAction,
} from "../../utils/pagePermissions";
import { getOccasionTheme } from "../../utils/occasionThemes";
import { getAuthToken } from "../../utils/authToken";
import {
  parseCapacity,
  sumOccasionCapacities,
  eventOccasionCapacityNotice,
  computeSeatLayout,
  seatLayoutCapacityNotice,
} from "../../utils/capacityLimits";
import { useEventCapacity } from "../../utils/useEventCapacity";
import CapacityNoticeModal from "../../components/CapacityNoticeModal";
import {
  validateRequiredFields,
  firstErrorMessage,
  focusFirstInvalidField,
  withInvalidClass,
} from "../../utils/formValidation";

const formatOccasionDateTime = (dateValue, timeValue) => {
  const dateLabel = dateValue
    ? new Date(dateValue).toLocaleDateString()
    : "—";
  const timeLabel = String(timeValue || "").trim();
  if (!timeLabel) return dateLabel;
  return `${dateLabel} · ${timeLabel}`;
};

const getMediaKind = (path) => {
  const ext = String(path || "")
    .split(".")
    .pop()
    .toLowerCase();
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "video";
  return "image";
};

const OccasionMaster = () => {
  const [searchTerm, setSearchTerm] = useState("");
  // States to manage form inputs
  const [OccasionName, setOccasionName] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [eventCapacity, setEventCapacity] = useState("");
  // State to manage the saved occasions
  const [dressCode, setDressCode] = useState("");
  const [mediaUpload, setMediaUpload] = useState(null);
  const [occasions, setOccasions] = useState([]);
  const [rsvpType, setRsvpType] = useState("");
  const [rsvp, setRsvp] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [invitationDropdown, setInvitationDropdown] = useState([]);
  const [capacityDropdown, setCapacityDropdown] = useState([]);
  const [giftDropdown, setGiftDropdown] = useState([]);
  const [rsvpDropdown, setRsvpDropdown] = useState([]);
  const [prefixDropdown, setPrefixDropdown] = useState([]);
  const [seatAllocationDropdown, setSeatAllocationDropdown] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isRsvpDropdownOpen, setIsRsvpDropdownOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const user = useSelector((state) => state.auth.user);
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const canEdit = canEditPage(selectedEvent, "occasion-master", user);
  const canDelete = canDeletePage(selectedEvent, "occasion-master", user);
  const canToggleActive = canSpecialAction(
    selectedEvent,
    "occasion-master",
    user
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editingOccasionId, setEditingOccasionId] = useState(null);
  const filteredOccasions = occasions.filter((occasion) =>
    occasion.OccasionName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // State for showing confirmation dialog for delete
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    occasionId: null,
    OccasionName: "",
  });

  const [occasionToggleConfirmation, setOccasionToggleConfirmation] =
    useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [capacityNotice, setCapacityNotice] = useState(null);
  const { eventCapacity: eventCapacityLimit, eventCapacityLabel } =
    useEventCapacity(selectedEvent?.eventId);

  const invitationRef = useRef(null);
  const rsvpRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        invitationRef.current &&
        !invitationRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
      if (rsvpRef.current && !rsvpRef.current.contains(event.target)) {
        setIsRsvpDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOccasionToggle = (occasion) => {
    const newStatus = !occasion.isOccasionActive;
    setOccasionToggleConfirmation({
      message: `Are you sure you want to ${
        newStatus ? "activate" : "deactivate"
      } this occasion?`,
      newStatus,
      occasionId: occasion._id,
    });
  };

  const initialFormData = {
    eventId: "",
    OccasionName: "",
    Date: "",
    time: "",
    day: "",
    OccasionCapacity: "",
    DressCode: "",
    InvitationType: [],
    Gifts: "",
    RSVP: false,
    RSVPType: [],
    isOccasionActive: true,
    seatAllocationType: "",
    numberOfRows: 0,
    numberOfSeatsPerRow: 0,
    numberOfTables: 0,
    numberOfSeatsPerTable: 0,
    hostname: {
      prefix: "",
      firstName: "",
      lastName: "",
      suffix: "",
      mobileNo: "",
      email: "",
      address: {
        line1: "",
        line2: "",
        line3: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
      },
    },
    venueAddress: {
      name: "",
      address: {
        line1: "",
        line2: "",
        line3: "",
        city: "",
        state: "",
        pincode: "",
        country: "",
      },
    },
  };
  const [formData, setFormData] = useState(initialFormData);

  const fetchOccasions = async () => {
    try {
      if (!selectedEvent?.eventId) return;

      const response = await axios.get(
        getOccasionsByEventId(selectedEvent.eventId)
      );
      console.log("Occasions fetched:", response.data.data);
      setOccasions(response.data.data);
    } catch (error) {
      console.error("Error fetching occasions:", error);
      if (error.response?.status === 404) {
        setOccasions([]);
      }
    }
  };

  useEffect(() => {
    fetchOccasions();
  }, [selectedEvent?.eventId]);

  const handleDelete = async () => {
    try {
      const response = await axios.delete(
        deleteOccasionData(deleteConfirmation.occasionId)
      );

      if (response.status === 200) {
        toast.success("Occasion deleted successfully.");
        await fetchOccasions(); // 🔁 Reuse here
      } else {
        toast.error("Failed to delete occasion.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Something went wrong while deleting.");
    } finally {
      setDeleteConfirmation({ isOpen: false, occasionId: null });
    }
  };

  const confirmOccasionToggle = async (confirmed) => {
    if (!confirmed) {
      setOccasionToggleConfirmation(null);
      return;
    }

    try {
      const { occasionId, newStatus } = occasionToggleConfirmation;

      const payload = { isOccasionActive: newStatus };

      await axios.put(updateOccasion(occasionId), payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(
        `Occasion successfully ${newStatus ? "activated" : "deactivated"}.`
      );
      await fetchOccasions(); // 🔁 Refresh the list (this should already be a shared function now)
    } catch (error) {
      console.error("Failed to update occasion status:", error);
      toast.error("Error updating occasion status.");
    } finally {
      setOccasionToggleConfirmation(null);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    // Handle checkbox
    if (name === "RSVP") {
      setFormData({
        ...formData,
        RSVP: checked,
        RSVPType: checked
          ? Array.isArray(formData.RSVPType)
            ? formData.RSVPType
            : []
          : [],
      });
      if (!checked) {
        setIsRsvpDropdownOpen(false);
        setFieldErrors((prev) => {
          if (!prev.RSVPType) return prev;
          const next = { ...prev };
          delete next.RSVPType;
          return next;
        });
      }
      return;
    }

    // Convert string to Date object for 'Date' field
    if (name === "Date") {
      setFormData({ ...formData, Date: new Date(value) });
      return;
    }

    // Handle Allocation Type changes to reset counts
    if (name === "seatAllocationType") {
      let updated = { ...formData, seatAllocationType: value.toLowerCase() };

      if (value.toLowerCase() === "row") {
        updated.numberOfTables = 0;
        updated.numberOfSeatsPerTable = 0;
      } else if (value.toLowerCase() === "table") {
        updated.numberOfRows = 0;
        updated.numberOfSeatsPerRow = 0;
      }
      // For "both", do nothing (keep all counts)
      setFormData(updated);
      return;
    }

    // Handle simple (flat) keys
    if (!name.includes(".")) {
      setFormData({ ...formData, [name]: value });
    } else {
      // Handle nested keys like "hostname.firstName"
      const keys = name.split(".");
      setFormData((prev) => {
        const updated = { ...prev };
        let nested = updated;
        keys.slice(0, -1).forEach((k) => {
          nested[k] = { ...nested[k] }; // Clone nested level
          nested = nested[k];
        });
        nested[keys.at(-1)] = value;
        return updated;
      });
    }

    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      {
        key: "OccasionName",
        value: formData.OccasionName,
        label: "Occasion Name",
      },
      { key: "Date", value: formData.Date, label: "Date" },
      { key: "time", value: formData.time, label: "Time" },
      {
        key: "OccasionCapacity",
        value: formData.OccasionCapacity,
        label: "Capacity (pax)",
      },
      {
        key: "InvitationType",
        value: formData.InvitationType,
        label: "Invitation Type",
      },
    ];

    if (formData.RSVP) {
      requiredFields.push({
        key: "RSVPType",
        value: formData.RSVPType,
        label: "RSVP Type",
      });
    }

    const errors = validateRequiredFields(requiredFields);
    const thisCapacity = parseCapacity(formData.OccasionCapacity);
    if (formData.OccasionCapacity && thisCapacity == null) {
      errors.OccasionCapacity = "Enter a numeric capacity";
    }
    if (eventCapacityLimit != null && thisCapacity != null) {
      const otherCapacity = sumOccasionCapacities(
        occasions,
        isEditing ? editingOccasionId : null
      );
      const combinedCapacity = otherCapacity + thisCapacity;
      if (combinedCapacity > eventCapacityLimit) {
        errors.OccasionCapacity = `Cannot exceed event capacity of ${eventCapacityLimit}`;
        setCapacityNotice(
          eventOccasionCapacityNotice({
            eventCapacity: eventCapacityLimit,
            combinedCapacity,
            thisCapacity,
            otherCapacity,
          })
        );
      }
    }
    if (thisCapacity != null) {
      const seatLayout = computeSeatLayout({
        ...formData,
        numberOfRows:
          String(formData.seatAllocationType || "").toLowerCase() === "table"
            ? 0
            : formData.numberOfRows,
        numberOfSeatsPerRow:
          String(formData.seatAllocationType || "").toLowerCase() === "table"
            ? 0
            : formData.numberOfSeatsPerRow,
        numberOfTables:
          String(formData.seatAllocationType || "").toLowerCase() === "row"
            ? 0
            : formData.numberOfTables,
        numberOfSeatsPerTable:
          String(formData.seatAllocationType || "").toLowerCase() === "row"
            ? 0
            : formData.numberOfSeatsPerTable,
      });
      // Equal to occasion capacity is allowed.
      if (seatLayout.totalSeats > thisCapacity) {
        errors.numberOfRows = "Seats exceed occasion capacity";
        setCapacityNotice(
          seatLayoutCapacityNotice({
            occasionCapacity: thisCapacity,
            ...seatLayout,
          })
        );
      }
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error(firstErrorMessage(errors));
      focusFirstInvalidField(errors, {
        rootSelector: "#formColumn",
        order: requiredFields.map((f) => f.key),
      });
      return;
    }

    setFieldErrors({});

    try {
      const formDataToSend = new FormData();

      // Normalize InvitationType and RSVPType as simple arrays
      const cleanInvitationType = Array.isArray(formData.InvitationType)
        ? formData.InvitationType.filter(Boolean)
        : [];
      const cleanRSVPType = Array.isArray(formData.RSVPType)
        ? formData.RSVPType.filter(Boolean)
        : [];

      formDataToSend.append("eventId", selectedEvent.eventId || "");
      formDataToSend.append("OccasionName", formData.OccasionName);
      formDataToSend.append(
        "Date",
        formData.Date instanceof Date
          ? formData.Date.toISOString().split("T")[0]
          : new Date(formData.Date).toISOString().split("T")[0]
      );
      formDataToSend.append("time", formData.time);
      formDataToSend.append("day", formData.day || "");
      formDataToSend.append("OccasionCapacity", formData.OccasionCapacity);
      formDataToSend.append("DressCode", formData.DressCode);
      formDataToSend.append(
        "InvitationType",
        JSON.stringify(cleanInvitationType)
      );
      formDataToSend.append("RSVPType", JSON.stringify(cleanRSVPType));
      formDataToSend.append("Gifts", formData.Gifts);
      formDataToSend.append("RSVP", formData.RSVP ? "true" : "false");
      formDataToSend.append("isOccasionActive", true);
      formDataToSend.append("hostname", JSON.stringify(formData.hostname));
      const submitSeatType = String(formData.seatAllocationType || "")
        .trim()
        .toLowerCase();
      const submitRows =
        submitSeatType === "table" ? 0 : formData.numberOfRows || 0;
      const submitSeatsPerRow =
        submitSeatType === "table" ? 0 : formData.numberOfSeatsPerRow || 0;
      const submitTables =
        submitSeatType === "row" ? 0 : formData.numberOfTables || 0;
      const submitSeatsPerTable =
        submitSeatType === "row" ? 0 : formData.numberOfSeatsPerTable || 0;
      formDataToSend.append("seatAllocationType", submitSeatType);
      formDataToSend.append("numberOfRows", submitRows);
      formDataToSend.append("numberOfSeatsPerRow", submitSeatsPerRow);
      formDataToSend.append("numberOfTables", submitTables);
      formDataToSend.append("numberOfSeatsPerTable", submitSeatsPerTable);
      formDataToSend.append(
        "venueAddress",
        JSON.stringify(formData.venueAddress)
      );

      if (mediaUpload) {
        formDataToSend.append("MediaUpload", mediaUpload);
      }

      const url = isEditing
        ? updateOccasion(editingOccasionId) // PUT URL
        : addOccasion(); // POST URL

      const response = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          ...(getAuthToken()
            ? { Authorization: `Bearer ${getAuthToken()}` }
            : {}),
        },
        body: formDataToSend,
      });

      if (response.ok) {
        toast.success(isEditing ? "Occasion updated!" : "Occasion created!");
        setIsEditing(false);
        setEditingOccasionId(null);
        setFormData({ ...initialFormData });
        setMediaUpload(null);
        await fetchOccasions(); // refresh table
      } else {
        let errBody = {};
        try {
          errBody = JSON.parse(await response.text());
        } catch {
          errBody = {};
        }
        if (errBody.code === "CAPACITY_EXCEEDED") {
          const lines = String(errBody.message || "")
            .split("\n")
            .map((line) => line.trim())
            .filter(Boolean);
          setCapacityNotice({
            title: lines[0] || "Capacity exceeded",
            message: lines.slice(1).join("\n") || errBody.message,
          });
        } else {
          toast.error(errBody.message || "Something went wrong.");
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Something went wrong.");
    }
  };

  useEffect(() => {
    const fetchDropdownData = async (lookupId, setDropdown, errorMessage) => {
      try {
        const result = await getLookupUtils(lookupId);
        setDropdown(result.data);

        // Log gift data if lookupId is "18"
        if (lookupId === "18") {
          console.log("Gift data fetched:", result.data);
        }
      } catch (error) {
        console.error(errorMessage, error);
      }
    };

    fetchDropdownData(
      "6",
      setInvitationDropdown,
      "Failed to fetch Invitation Type:"
    );
    fetchDropdownData("1", setCapacityDropdown, "Failed to fetch Capacity:");
    fetchDropdownData("18", setGiftDropdown, "Failed to fetch Gift Data:");
    fetchDropdownData("7", setRsvpDropdown, "Failed to fetch Gift Data:");
    fetchDropdownData("3", setPrefixDropdown, "Failed to fetch Prefix Data:");
    fetchDropdownData(
      "24",
      setSeatAllocationDropdown,
      "Failed to fetch Allocation Type:"
    );
  }, []);

  // Handle edit button click
  const handleEdit = (occasion) => {
    const parsedDate = occasion.Date ? new Date(occasion.Date) : new Date();
    const parsedTime = occasion.time || "";

    // Parse InvitationType
    let parsedInvitationType = [];
    if (occasion.InvitationType) {
      try {
        parsedInvitationType = []
          .concat(
            ...occasion.InvitationType.map((item) =>
              typeof item === "string" && item.startsWith("[")
                ? JSON.parse(item)
                : item
            )
          )
          .filter(Boolean);
      } catch (err) {
        parsedInvitationType = [];
      }
    }

    // Parse RSVPType
    let parsedRSVPType = [];
    if (occasion.RSVPType) {
      try {
        parsedRSVPType = []
          .concat(
            ...occasion.RSVPType.map((item) =>
              typeof item === "string" && item.startsWith("[")
                ? JSON.parse(item)
                : item
            )
          )
          .filter(Boolean);
      } catch (err) {
        parsedRSVPType = [];
      }
    }

    setIsEditing(true);
    setEditingOccasionId(occasion._id);

    setFormData({
      ...occasion,
      Date: parsedDate,
      time: parsedTime,
      RSVP: occasion.RSVP === true || occasion.RSVP === "true",
      RSVPType: parsedRSVPType,
      InvitationType: parsedInvitationType,
      seatAllocationType: occasion.seatAllocationType || "",
      numberOfRows: occasion.numberOfRows || 0,
      numberOfSeatsPerRow: occasion.numberOfSeatsPerRow || 0,
      numberOfTables: occasion.numberOfTables || 0,
      numberOfSeatsPerTable: occasion.numberOfSeatsPerTable || 0,
    });

    setStartDate(parsedDate);
    setMediaUpload(occasion.MediaUpload || null);
    setRsvp(occasion.RSVP === true || occasion.RSVP === "true");
    setRsvpType(parsedRSVPType);
    setDressCode(occasion.DressCode);
    setEventCapacity(occasion.OccasionCapacity);
    setOccasionName(occasion.OccasionName);
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return "";
    const dateObj =
      typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    return dateObj.toISOString().split("T")[0]; // returns YYYY-MM-DD
  };

  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);

  const handleAddEventClick = () => {
    setIsSidebarExpanded((prev) => !prev);
  };

  const handleFileChange = (e, setFileName, setFile) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFileName(selectedFile.name);
      setFile(selectedFile);
    } else {
      setFileName("");
      setFile(null);
    }
  };
  // Function to export occasions
  const handleExportToExcel = () => {
    if (!filteredOccasions || filteredOccasions.length === 0) {
      toast.error("No occasions to export.");
      return;
    }

    // Map filteredOccasions to desired format
    const dataToExport = filteredOccasions.map((occasion) => ({
      Occasion: occasion.OccasionName || "",
      Date: occasion.Date ? new Date(occasion.Date).toLocaleDateString() : "",
      Time: occasion.time || "",
      Capacity: occasion.OccasionCapacity || "",
      "Dress Code": occasion.DressCode || "",
      "Invitation Type": Array.isArray(occasion.InvitationType)
        ? occasion.InvitationType.join(", ")
        : occasion.InvitationType || "",
      Gifts: occasion.Gifts || "",
      RSVPType:
        occasion.RSVP === true || occasion.RSVP === "true"
          ? Array.isArray(occasion.RSVPType)
            ? occasion.RSVPType.join(", ")
            : occasion.RSVPType || ""
          : "",
    }));

    // Create a worksheet
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Optional: Set header style
    const headerCells = Object.keys(dataToExport[0]);
    headerCells.forEach((key, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      if (!worksheet[cellRef]) return;
      worksheet[cellRef].s = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "C6EFCE" } },
        alignment: { horizontal: "center" },
      };
    });

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Occasions");

    // Export to Excel file
    XLSX.writeFile(workbook, "Occasions.xlsx");
  };

  const occasionPax = parseCapacity(formData.OccasionCapacity);
  const liveSeatLayout = computeSeatLayout(formData);
  const seatTotalOver =
    occasionPax != null && liveSeatLayout.totalSeats > occasionPax;
  const remainingSeats =
    occasionPax == null
      ? null
      : Math.max(0, occasionPax - liveSeatLayout.totalSeats);
  const overBy = seatTotalOver
    ? liveSeatLayout.totalSeats - occasionPax
    : 0;
  const seatType = String(formData.seatAllocationType || "")
    .trim()
    .toLowerCase();
  const hasRowSeatData =
    Number(formData.numberOfRows) > 0 &&
    Number(formData.numberOfSeatsPerRow) > 0;
  const hasTableSeatData =
    Number(formData.numberOfTables) > 0 &&
    Number(formData.numberOfSeatsPerTable) > 0;
  const showSeatTotals =
    seatType === "row"
      ? hasRowSeatData
      : seatType === "table"
        ? hasTableSeatData
        : seatType === "both"
          ? hasRowSeatData || hasTableSeatData
          : false;
  const showRowSeating = seatType === "row" || seatType === "both";
  const showTableSeating = seatType === "table" || seatType === "both";
  const editingOccasionIndex = occasions.findIndex(
    (item) => String(item._id) === String(editingOccasionId)
  );
  const editingTheme = getOccasionTheme(
    editingOccasionIndex >= 0 ? editingOccasionIndex : 0
  );

  return (
    <>
      <div className="container-fluid">


        <div className="Occasion_Master">
            <div
              className={`occasion-top occasion-top-with-sidebar row g-4 align-items-center${
                isSidebarExpanded ? " expanded-sidebar" : ""
              }`}
            >
              <div className={isEditing ? "col-lg-4 occasion-top-title-col" : "col-lg-9"}>
                <h2 className="mb-0">Occasion Master</h2>
              </div>
              {isEditing ? (
                <div className="col-lg-5 occasion-top-edit-col">
                  <div
                    className="occasion-edit-name-banner"
                    style={{
                      "--card-accent": editingTheme.primary,
                      "--card-accent-dark": editingTheme.primaryDark,
                      "--card-soft": editingTheme.soft,
                      "--card-border": editingTheme.border,
                    }}
                  >
                    {formData.OccasionName || "Editing occasion"}
                  </div>
                </div>
              ) : null}
              <div className="col-lg-3 occasion-top-search-col">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Search Occasion Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          <div className="">
            {/* Form Section */}
            <div
              className={`row ${
                isSidebarExpanded ? "expanded-sidebar" : ""
              }`}
            >
              <div className="col-lg-9" id="formColumn">
                <div className="form-section">
                  <h5 className="event-inner-hed">
                    {isEditing ? "Edit Occasion" : "Add Occasion"}
                  </h5>
                  <hr />
                  <div className="all-page-scroll">
                    {!canEdit ? (
                      <p className="text-muted mt-3">
                        You have view-only access. Select Edit permission in User
                        Access to create or update occasions.
                      </p>
                    ) : (
                    <form onSubmit={handleSubmit}>
                      <div className="row g-3 my-3">
                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Occasion <span className="required-asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            name="OccasionName"
                            value={formData.OccasionName}
                            onChange={handleChange}
                            className={withInvalidClass(
                              "form-control",
                              !!fieldErrors.OccasionName
                            )}
                            placeholder="Enter occasion name"
                          />
                          {fieldErrors.OccasionName ? (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.OccasionName}
                            </div>
                          ) : null}
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Date <span className="required-asterisk">*</span>
                          </label>
                          <input
                            type="date"
                            name="Date"
                            value={formatDateForInput(formData.Date)}
                            onChange={handleChange}
                            className={withInvalidClass(
                              "form-control",
                              !!fieldErrors.Date
                            )}
                          />
                          {fieldErrors.Date ? (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.Date}
                            </div>
                          ) : null}
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Time <span className="required-asterisk">*</span>
                          </label>
                          <input
                            type="time"
                            name="time"
                            value={formData.time}
                            onChange={handleChange}
                            className={withInvalidClass(
                              "form-control",
                              !!fieldErrors.time
                            )}
                          />
                          {fieldErrors.time ? (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.time}
                            </div>
                          ) : null}
                        </div>

                        {/* <div className="col-md-4 mb-4">
                          <label className="form-label">
                          Capacity
                          </label>
                          <select
                            className="form-select"
                            name="OccasionCapacity"
                            value={formData.OccasionCapacity}
                            onChange={handleChange}
                          >
                            <option value="">Select Capacity</option>{" "}
                            
                            {capacityDropdown.map((item) => (
                              <option key={item._id} value={item.Lookup_Value_Code}>
                                {" "}
                                {item.Lookup_Value_Name}
                              </option>
                            ))}
                          </select>
                        </div> */}
                        <div className="col-md-4 mb-3">
                          <label className="form-label">
                            Capacity (pax){" "}
                            <span className="required-asterisk">*</span>
                          </label>
                          <input
                            type="text"
                            className={withInvalidClass(
                              "form-control",
                              !!fieldErrors.OccasionCapacity
                            )}
                            name="OccasionCapacity"
                            value={formData.OccasionCapacity}
                            onChange={handleChange}
                            placeholder="Enter capacity"
                          />
                          {fieldErrors.OccasionCapacity ? (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.OccasionCapacity}
                            </div>
                          ) : eventCapacityLimit != null ? (
                            <div className="form-text">
                              Event capacity is{" "}
                              {eventCapacityLabel || eventCapacityLimit}.
                              Other occasions use{" "}
                              {sumOccasionCapacities(
                                occasions,
                                isEditing ? editingOccasionId : null
                              )}
                              . This occasion can be up to{" "}
                              {Math.max(
                                0,
                                eventCapacityLimit -
                                  sumOccasionCapacities(
                                    occasions,
                                    isEditing ? editingOccasionId : null
                                  )
                              )}
                              .
                            </div>
                          ) : null}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Dress Code</label>
                          <input
                            type="text"
                            name="DressCode"
                            value={formData.DressCode}
                            onChange={handleChange}
                            className="form-control"
                            placeholder="Enter Dress Code"
                          />
                        </div>
                        <div
                          className="col-md-4 mb-3"
                          ref={invitationRef}
                          style={{ position: "relative" }}
                        >
                          <label className="form-label">
                            Invitation Type{" "}
                            <span className="required-asterisk">*</span>
                          </label>
                          <div
                            className={withInvalidClass(
                              "border rounded p-2 field-invalid-wrap",
                              !!fieldErrors.InvitationType
                            )}
                            data-field="InvitationType"
                            style={{ cursor: "pointer", userSelect: "none" }}
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <span>
                                {formData.InvitationType.length > 0
                                  ? `${formData.InvitationType.length} selected`
                                  : "Select Invitation Type"}
                              </span>
                              {isDropdownOpen ? (
                                <FaChevronUp />
                              ) : (
                                <FaChevronDown />
                              )}
                            </div>
                          </div>

                          {isDropdownOpen && (
                            <div
                              style={{
                                position: "absolute",
                                top: "100%", // positions right below the input box
                                left: 0,
                                right: 0,
                                zIndex: 1000, // makes sure it stays above other elements
                                maxHeight: "200px",
                                overflowY: "auto",
                                marginTop: "5px",
                                background: "white",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                                padding: "10px",
                              }}
                              onClick={(e) => e.stopPropagation()} // Prevent dropdown from closing when clicking inside
                            >
                              {invitationDropdown.length === 0 ? (
                                <p>Loading...</p>
                              ) : (
                                invitationDropdown.map((item) => (
                                  <div key={item._id} className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      id={`invitation-${item._id}`}
                                      value={item.Lookup_Value_Code}
                                      checked={formData.InvitationType.includes(
                                        item.Lookup_Value_Code
                                      )}
                                      onChange={(e) => {
                                        const value = item.Lookup_Value_Code;
                                        setFormData((prev) => {
                                          const current = Array.isArray(
                                            prev.InvitationType
                                          )
                                            ? prev.InvitationType
                                            : [];
                                          if (e.target.checked) {
                                            return {
                                              ...prev,
                                              InvitationType: [
                                                ...current,
                                                value,
                                              ],
                                            };
                                          } else {
                                            return {
                                              ...prev,
                                              InvitationType: current.filter(
                                                (v) => v !== value
                                              ),
                                            };
                                          }
                                        });
                                        setFieldErrors((prev) => {
                                          if (!prev.InvitationType) return prev;
                                          const next = { ...prev };
                                          delete next.InvitationType;
                                          return next;
                                        });
                                      }}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`invitation-${item._id}`}
                                    >
                                      {item.Lookup_Value_Name}
                                    </label>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                          {fieldErrors.InvitationType ? (
                            <div className="invalid-feedback d-block">
                              {fieldErrors.InvitationType}
                            </div>
                          ) : null}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label" htmlFor="mediaUpload">
                            Media (Upload)
                          </label>
                          <input
                            type="file"
                            id="mediaUpload"
                            className="form-control"
                            onChange={(e) =>
                              handleFileChange(e, setFileName, setMediaUpload)
                            }
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Gifts</label>
                          <select
                            className="form-select"
                            name="Gifts"
                            value={formData.Gifts}
                            onChange={(e) => {
                              console.log("Selected gift:", e.target.value);
                              handleChange(e);
                            }}
                          >
                            <option value="">Select Gifts</option>{" "}
                            {/* Default option */}
                            {giftDropdown.map((item) => (
                              <option
                                key={item._id}
                                value={item.Lookup_Value_Code}
                              >
                                {" "}
                                {item.Lookup_Value_Name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="occasion-rsvp-section">
                            <div className="occasion-rsvp-toggle-row">
                              <div>
                                <div className="occasion-rsvp-section-title">
                                  RSVP
                                </div>
                                <p className="occasion-rsvp-section-hint mb-0">
                                  {formData.RSVP
                                    ? "Guests can respond to this occasion"
                                    : "Turn on to collect RSVP responses"}
                                </p>
                              </div>
                              <div className="form-check form-switch occasion-rsvp-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="rsvpCheck"
                                  name="RSVP"
                                  checked={!!formData.RSVP}
                                  onChange={handleChange}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="rsvpCheck"
                                >
                                  {formData.RSVP ? "On" : "Off"}
                                </label>
                              </div>
                            </div>

                            <div
                              className={`occasion-rsvp-type${
                                formData.RSVP ? "" : " is-disabled"
                              }`}
                              ref={rsvpRef}
                            >
                              <label className="form-label mb-1">
                                RSVP Type
                                {formData.RSVP ? (
                                  <>
                                    {" "}
                                    <span className="required-asterisk">*</span>
                                  </>
                                ) : null}
                              </label>
                              <div
                                className={withInvalidClass(
                                  "border rounded p-2 field-invalid-wrap occasion-rsvp-type-trigger",
                                  !!fieldErrors.RSVPType
                                )}
                                data-field="RSVPType"
                                onClick={() => {
                                  if (!formData.RSVP) {
                                    toast.error(
                                      "Turn on RSVP first to select a type."
                                    );
                                    return;
                                  }
                                  setIsRsvpDropdownOpen((prev) => !prev);
                                }}
                              >
                                <div className="d-flex justify-content-between align-items-center">
                                  <span>
                                    {Array.isArray(formData.RSVPType) &&
                                    formData.RSVPType.length > 0
                                      ? `${formData.RSVPType.length} selected`
                                      : "Select RSVP Type"}
                                  </span>
                                  {isRsvpDropdownOpen ? (
                                    <FaChevronUp />
                                  ) : (
                                    <FaChevronDown />
                                  )}
                                </div>
                              </div>

                              {isRsvpDropdownOpen && formData.RSVP && (
                                <div
                                  className="occasion-rsvp-type-menu"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {rsvpDropdown.length === 0 ? (
                                    <p className="mb-0 text-muted">Loading…</p>
                                  ) : (
                                    rsvpDropdown.map((item) => (
                                      <div
                                        key={item._id}
                                        className="form-check occasion-theme-check"
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input"
                                          id={`rsvp-${item._id}`}
                                          value={item.Lookup_Value_Code}
                                          checked={
                                            Array.isArray(formData.RSVPType) &&
                                            formData.RSVPType.includes(
                                              item.Lookup_Value_Code
                                            )
                                          }
                                          onChange={(e) => {
                                            const value =
                                              item.Lookup_Value_Code;
                                            setFormData((prev) => {
                                              const current = Array.isArray(
                                                prev.RSVPType
                                              )
                                                ? prev.RSVPType
                                                : [];
                                              if (e.target.checked) {
                                                return {
                                                  ...prev,
                                                  RSVPType: [
                                                    ...current,
                                                    value,
                                                  ],
                                                };
                                              }
                                              return {
                                                ...prev,
                                                RSVPType: current.filter(
                                                  (v) => v !== value
                                                ),
                                              };
                                            });
                                            setFieldErrors((prev) => {
                                              if (!prev.RSVPType) return prev;
                                              const next = { ...prev };
                                              delete next.RSVPType;
                                              return next;
                                            });
                                          }}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`rsvp-${item._id}`}
                                        >
                                          {item.Lookup_Value_Name}
                                        </label>
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                              {fieldErrors.RSVPType ? (
                                <div className="invalid-feedback d-block">
                                  {fieldErrors.RSVPType}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <h5 className="event-inner-hed mt-3">
                          Seat Allocation
                        </h5>
                        <hr />

                        <div className="col-12 col-md-4 mb-3">
                          <label className="form-label">Allocation Type</label>
                          <select
                            className="form-select"
                            name="seatAllocationType"
                            value={formData.seatAllocationType || ""}
                            onChange={handleChange}
                          >
                            <option value="">Select Allocation Type</option>
                            {seatAllocationDropdown.map((item) => (
                              <option
                                key={item._id}
                                value={item.Lookup_Value_Code.toLowerCase()}
                              >
                                {item.Lookup_Value_Name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {showSeatTotals ? (
                          <div className="col-12 mb-3">
                            <div
                              className={`occasion-seat-total${
                                seatTotalOver ? " is-over" : ""
                              }`}
                            >
                              <div className="occasion-seat-total-title">
                                Seat summary
                              </div>
                              <div className="occasion-seat-total-grid">
                                <div className="occasion-seat-total-stat">
                                  <span className="occasion-seat-total-label">
                                    Total seats
                                  </span>
                                  <span className="occasion-seat-total-value">
                                    {liveSeatLayout.totalSeats}
                                  </span>
                                </div>
                                {occasionPax != null ? (
                                  <div className="occasion-seat-total-stat">
                                    <span className="occasion-seat-total-label">
                                      Occasion capacity
                                    </span>
                                    <span className="occasion-seat-total-value">
                                      {occasionPax}
                                      <span className="occasion-seat-total-meta">
                                        {seatTotalOver
                                          ? `· ${overBy} over`
                                          : `· remaining ${remainingSeats}`}
                                      </span>
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                              {(showRowSeating && hasRowSeatData) ||
                              (showTableSeating && hasTableSeatData) ? (
                                <div className="occasion-seat-total-split">
                                  {showRowSeating && hasRowSeatData ? (
                                    <div className="occasion-seat-total-part">
                                      <span className="occasion-seat-total-label">
                                        Row seats
                                      </span>
                                      <span className="occasion-seat-total-value">
                                        {liveSeatLayout.rowSeats}
                                        <span className="occasion-seat-total-meta">
                                          ({formData.numberOfRows} ×{" "}
                                          {formData.numberOfSeatsPerRow})
                                        </span>
                                      </span>
                                    </div>
                                  ) : null}
                                  {showTableSeating && hasTableSeatData ? (
                                    <div className="occasion-seat-total-part">
                                      <span className="occasion-seat-total-label">
                                        Table seats
                                      </span>
                                      <span className="occasion-seat-total-value">
                                        {liveSeatLayout.tableSeats}
                                        <span className="occasion-seat-total-meta">
                                          ({formData.numberOfTables} ×{" "}
                                          {formData.numberOfSeatsPerTable})
                                        </span>
                                      </span>
                                    </div>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        {showRowSeating || showTableSeating ? (
                          <div className="col-12 mb-3">
                            <div
                              className={`occasion-seat-panels${
                                showRowSeating && showTableSeating
                                  ? " is-split"
                                  : ""
                              }`}
                            >
                              {showRowSeating ? (
                                <div className="occasion-seat-group">
                                  <div className="occasion-seat-group-title">
                                    Row seating
                                  </div>
                                  <div className="occasion-seat-group-fields">
                                    <div className="occasion-seat-field">
                                      <label className="form-label">
                                        Select Number of Rows
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        name="numberOfRows"
                                        value={formData.numberOfRows || ""}
                                        onChange={handleChange}
                                        placeholder="e.g. 10"
                                      />
                                    </div>
                                    <div className="occasion-seat-field">
                                      <label className="form-label">
                                        Select Number of Seats per Row
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        name="numberOfSeatsPerRow"
                                        value={
                                          formData.numberOfSeatsPerRow || ""
                                        }
                                        onChange={handleChange}
                                        placeholder="e.g. 12"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : null}

                              {showTableSeating ? (
                                <div className="occasion-seat-group">
                                  <div className="occasion-seat-group-title">
                                    Table seating
                                  </div>
                                  <div className="occasion-seat-group-fields">
                                    <div className="occasion-seat-field">
                                      <label className="form-label">
                                        Select Number of Tables
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        name="numberOfTables"
                                        value={formData.numberOfTables || ""}
                                        onChange={handleChange}
                                        placeholder="e.g. 8"
                                      />
                                    </div>
                                    <div className="occasion-seat-field">
                                      <label className="form-label">
                                        Select Number of Seats per Table
                                      </label>
                                      <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        name="numberOfSeatsPerTable"
                                        value={
                                          formData.numberOfSeatsPerTable || ""
                                        }
                                        onChange={handleChange}
                                        placeholder="e.g. 6"
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}

                        <h5 className="event-inner-hed mt-3">
                          Host Details
                        </h5>

                        <hr />

                        <div class="col-md-4 mb-3">
                          <label className="form-label">Prefix</label>
                          <select
                            className="form-select"
                            name="hostname.prefix"
                            value={formData.hostname.prefix}
                            onChange={handleChange}
                          >
                            <option value="">Select Prefix</option>
                            {prefixDropdown.map((item) => (
                              <option
                                key={item._id}
                                value={item.Lookup_Value_Code}
                              >
                                {item.Lookup_Value_Name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">First Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter First Name"
                            name="hostname.firstName"
                            value={formData.hostname.firstName}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Last Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Last Name"
                            name="hostname.lastName"
                            value={formData.hostname.lastName}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Suffix</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Suffix"
                            name="hostname.suffix"
                            value={formData.hostname.suffix}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Mobile Number</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Mobile Number"
                            name="hostname.mobileNo"
                            value={formData.hostname.mobileNo}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Email</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Email"
                            name="hostname.email"
                            value={formData.hostname.email}
                            onChange={handleChange}
                          />
                        </div>
                        <h5 className="event-inner-hed mt-3">Host Address</h5>
                        <hr />
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Line 1</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Address Line 1"
                            name="hostname.address.line1"
                            value={formData.hostname.address.line1}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Line 2</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Address Line 2"
                            name="hostname.address.line2"
                            value={formData.hostname.address.line2}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Line 3</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Address Line 3"
                            name="hostname.address.line3"
                            value={formData.hostname.address.line3}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter City"
                            name="hostname.address.city"
                            value={formData.hostname.address.city}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">State</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter State"
                            name="hostname.address.state"
                            value={formData.hostname.address.state}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Pincode</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Pincode"
                            name="hostname.address.pincode"
                            value={formData.hostname.address.pincode}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Country</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Country"
                            name="hostname.address.country"
                            value={formData.hostname.address.country}
                            onChange={handleChange}
                          />
                        </div>
                        <h5 className="event-inner-hed mt-3">Venue Address</h5>
                        <hr />

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Name</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Venue Name"
                            name="venueAddress.name"
                            value={formData.venueAddress.name}
                            onChange={handleChange}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Line 1</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Address Line 1"
                            name="venueAddress.address.line1"
                            value={formData.venueAddress.address.line1}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Line 2</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Address Line 2"
                            name="venueAddress.address.line2"
                            value={formData.venueAddress.address.line2}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Line 3</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Address Line 3"
                            name="venueAddress.address.line3"
                            value={formData.venueAddress.address.line3}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">City</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter City"
                            name="venueAddress.address.city"
                            value={formData.venueAddress.address.city}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">State</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter State"
                            name="venueAddress.address.state"
                            value={formData.venueAddress.address.state}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Pincode</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Pincode"
                            name="venueAddress.address.pincode"
                            value={formData.venueAddress.address.pincode}
                            onChange={handleChange}
                          />
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label">Country</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Country"
                            name="venueAddress.address.country"
                            value={formData.venueAddress.address.country}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </form>
                    )}
                  </div>
                  {canEdit && (
                  <>
                  <hr />
                  <div className="d-flex justify-content-end gap-2 justify-content-center py-2">
                    <button
                      type="submit"
                      className="btn btn-success btn-save"
                      onClick={handleSubmit}
                    >
                      {isEditing ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-cancel"
                      onClick={() => {
                        setIsEditing(false);
                        setEditingOccasionId(null);
                        setFormData({ ...initialFormData }); // define this above
                        setMediaUpload(null);
                        setIsSidebarExpanded(false);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                  </>
                  )}
                </div>
              </div>

              {/* Occasions List Section */}

              <div className="col-lg-3" id="sidebarColumn">
                <div className="event-sidebar">
                  <div className="guest-list-toolbar">
                    <h6 className="mb-0 event-inner-hed">All Occasions</h6>
                    <button
                      type="button"
                      className="guest-sidebar-toggle"
                      onClick={handleAddEventClick}
                      title={isSidebarExpanded ? "Collapse list" : "Expand list"}
                      aria-label={
                        isSidebarExpanded ? "Collapse list" : "Expand list"
                      }
                    >
                      <i
                        className={`bi ${
                          isSidebarExpanded
                            ? "bi-chevron-right"
                            : "bi-chevron-left"
                        }`}
                      ></i>
                    </button>
                  </div>
                  <hr />
                  <div className="all-page-right-scroll">
                    {/* Example Occasion Items */}
                    {/* <div className="occasion-item">
                      <strong>OccasionName</strong> <br />
                      <small>Date</small> <br />
                      <small>DressCode</small> <br />
                      <small>Capacity</small> <br />
                      <a href="#" className="text-primary me-2">
                        Edit
                      </a>
                      <a href="#" className="text-danger">
                        Delete
                      </a>
                    </div> */}

                    {isSidebarExpanded ? (
                      <div className="event-list-scroll">
                        <div className="table-responsive">
                          <table className="table">
                            <thead className="thead-dark table-bordered table-hover table-bg-new">
                              <tr>
                                <th>Sn.</th>
                                <th>Occasion</th>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Capacity</th>
                                <th>Dress&nbsp;Code</th>
                                <th>Invitation&nbsp;Type</th>
                                {/* <th>RSVP&nbsp;Type</th> */}
                                <th>Media</th>
                                <th>Gifts</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredOccasions.length > 0 ? (
                                filteredOccasions.map((occasion, index) => (
                                  <tr key={occasion._id}>
                                    <td>{index + 1}</td>
                                    <td>{occasion.OccasionName}</td>
                                    <td>
                                      {new Date(
                                        occasion.Date
                                      ).toLocaleDateString()}
                                    </td>
                                    <td>{occasion.time}</td>
                                    <td>{occasion.OccasionCapacity}</td>
                                    <td>{occasion.DressCode}</td>

                                    {/* Invitation Type Column */}
                                    <td>
                                      {Array.isArray(occasion.InvitationType) &&
                                      occasion.InvitationType.length > 0
                                        ? occasion.InvitationType.map(
                                            (type, idx) => (
                                              <div key={idx}>{type}</div>
                                            )
                                          )
                                        : "N/A"}
                                    </td>

                                    {/* RSVP Type Column */}
                                    {/* <td>
                                      {(occasion.RSVP === true ||
                                        occasion.RSVP === "true") &&
                                      Array.isArray(occasion.RSVPType) &&
                                      occasion.RSVPType.length > 0
                                        ? occasion.RSVPType.map((rsvp, idx) => (
                                            <div key={idx}>{rsvp}</div>
                                          ))
                                        : "N/A"}
                                    </td> */}

                                    <td>
                                      {occasion.MediaUpload
                                        ? (() => {
                                            const mediaPath =
                                              occasion.MediaUpload;
                                            const kind = getMediaKind(mediaPath);
                                            const mediaUrl = `${FRONTEND_URL}/${mediaPath}`;
                                            if (kind === "video") {
                                              return (
                                                <button
                                                  type="button"
                                                  className="btn btn-link p-0 occasion-media-trigger"
                                                  title="Preview video"
                                                  onClick={() =>
                                                    setMediaPreview({
                                                      url: mediaUrl,
                                                      kind: "video",
                                                      name: occasion.OccasionName,
                                                    })
                                                  }
                                                >
                                                  <FaVideo
                                                    className="occasion-expanded-media-icon"
                                                    title="Video file"
                                                  />
                                                </button>
                                              );
                                            }
                                            return (
                                              <button
                                                type="button"
                                                className="btn btn-link p-0 occasion-media-trigger"
                                                title="Preview media"
                                                onClick={() =>
                                                  setMediaPreview({
                                                    url: mediaUrl,
                                                    kind: "image",
                                                    name: occasion.OccasionName,
                                                  })
                                                }
                                              >
                                                <img
                                                  src={mediaUrl}
                                                  alt="media"
                                                  className="img-fluid-2 occasion-expanded-media"
                                                />
                                              </button>
                                            );
                                          })()
                                        : "N/A"}
                                    </td>

                                    <td>{occasion.Gifts}</td>

                                    <td
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: "0.5rem",
                                      }}
                                    >
                                      {canEdit && (
                                      <a
                                        href="#"
                                        className="btn edit-btn"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleEdit(occasion);
                                        }}
                                      >
                                        Edit
                                      </a>
                                      )}

                                      {canToggleActive && (
                                        occasion.isOccasionActive ? (
                                        <a
                                          href="#"
                                          className="btn deactivate-btn"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleOccasionToggle(occasion);
                                          }}
                                        >
                                          Deactivate
                                        </a>
                                      ) : (
                                        <a
                                          href="#"
                                          className="btn btn-success"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            handleOccasionToggle(occasion);
                                          }}
                                        >
                                          Activate
                                        </a>
                                      )
                                      )}

                                      {canDelete && (
                                      <a
                                        href="#"
                                        className="btn btn-danger"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setDeleteConfirmation({
                                            isOpen: true,
                                            occasionId: occasion._id,
                                            OccasionName: occasion.OccasionName,
                                          });
                                        }}
                                      >
                                        Delete
                                      </a>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="11" className="text-center">
                                    No matching occasions found.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="occasion-list list-side-stack">
                        {filteredOccasions.length > 0 ? (
                          filteredOccasions.map((occasion, index) => {
                            const theme = getOccasionTheme(index);
                            return (
                              <div
                                className="occasion-item list-side-card occasion-side-card"
                                key={occasion._id}
                                style={{
                                  "--card-accent": theme.primary,
                                  "--card-accent-dark": theme.primaryDark,
                                  "--card-soft": theme.soft,
                                  "--card-border": theme.border,
                                }}
                              >
                                <div className="list-side-card-title">
                                  {occasion.OccasionName}
                                </div>
                                <div className="list-side-card-meta">
                                  <span>
                                    <em>Date &amp; Time</em>
                                    {formatOccasionDateTime(
                                      occasion.Date,
                                      occasion.time
                                    )}
                                  </span>
                                  <span>
                                    <em>Capacity</em>
                                    {occasion.OccasionCapacity || "—"}
                                  </span>
                                  <span>
                                    <em>Dress Code</em>
                                    {occasion.DressCode || "—"}
                                  </span>
                                </div>
                                <div className="list-side-card-actions">
                                  {canEdit && (
                                  <a
                                    href="#"
                                    className="btn edit-btn"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleEdit(occasion);
                                    }}
                                  >
                                    Edit
                                  </a>
                                  )}
                                  {canToggleActive && (
                                    occasion.isOccasionActive ? (
                                    <a
                                      href="#"
                                      className="btn deactivate-btn"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleOccasionToggle(occasion);
                                      }}
                                    >
                                      Deactivate
                                    </a>
                                  ) : (
                                    <a
                                      href="#"
                                      className="btn btn-enable"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleOccasionToggle(occasion);
                                      }}
                                    >
                                      Activate
                                    </a>
                                  )
                                  )}
                                  {canDelete && (
                                  <a
                                    href="#"
                                    className="btn btn-danger"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setDeleteConfirmation({
                                        isOpen: true,
                                        occasionId: occasion._id,
                                        OccasionName: occasion.OccasionName,
                                      });
                                    }}
                                  >
                                    Delete
                                  </a>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted mb-0">No matching occasions found.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="guest-sidebar-actions">
                    <button
                      type="button"
                      className="btn guest-sidebar-action-btn is-export"
                      onClick={handleExportToExcel}
                    >
                      <i className="bi bi-file-earmark-excel" aria-hidden="true" />
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {deleteConfirmation.isOpen && (
              <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Confirm Delete</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() =>
                          setDeleteConfirmation({
                            isOpen: false,
                            occasionId: null,
                          })
                        }
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p>
                        Are you sure you want to delete the occasion:{" "}
                        <strong>{deleteConfirmation.OccasionName}</strong>?
                      </p>
                    </div>
                    <div className="modal-footer">
                      <button
                        type="button"
                        className="btn btn-secondary cancel-btn"
                        onClick={() =>
                          setDeleteConfirmation({
                            isOpen: false,
                            occasionId: null,
                          })
                        }
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDelete}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {occasionToggleConfirmation && (
              <div
                className="modal fade show d-block"
                tabIndex="-1"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Confirm Action</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setOccasionToggleConfirmation(null)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <p>{occasionToggleConfirmation.message}</p>
                    </div>
                    <div className="modal-footer">
                      <button
                        className="btn btn-secondary cancel-btn"
                        onClick={() => setOccasionToggleConfirmation(null)}
                      >
                        Cancel
                      </button>
                      <button
                        className={`btn ${
                          occasionToggleConfirmation.newStatus
                            ? "btn-enable"
                            : "btn-danger"
                        }`}
                        onClick={() => confirmOccasionToggle(true)}
                      >
                        {occasionToggleConfirmation.newStatus
                          ? "Activate"
                          : "Deactivate"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {mediaPreview && (
              <div
                className="modal fade show d-block occasion-media-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                style={{ backgroundColor: "rgba(0, 0, 0, 0.55)" }}
                onClick={() => setMediaPreview(null)}
              >
                <div
                  className="modal-dialog modal-dialog-centered modal-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">
                        {mediaPreview.name
                          ? `${mediaPreview.name} · Media`
                          : "Occasion Media"}
                      </h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={() => setMediaPreview(null)}
                      />
                    </div>
                    <div className="modal-body occasion-media-modal-body">
                      {mediaPreview.kind === "video" ? (
                        <video
                          src={mediaPreview.url}
                          controls
                          autoPlay
                          className="occasion-media-preview-video"
                        />
                      ) : (
                        <img
                          src={mediaPreview.url}
                          alt={mediaPreview.name || "Occasion media"}
                          className="occasion-media-preview-image"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <CapacityNoticeModal
              open={Boolean(capacityNotice)}
              title={capacityNotice?.title}
              message={capacityNotice?.message}
              rows={capacityNotice?.rows}
              hint={capacityNotice?.hint}
              onClose={() => setCapacityNotice(null)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default OccasionMaster;
